import { useFragment } from '@apollo/client/react';
import {
  createContext,
  useContext,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';

import type { PremiumFeatures } from '@trello/graphql';

import { BoardPremiumFeaturesFragmentDoc } from './BoardPremiumFeaturesFragment.generated';

const BoardPremiumFeaturesContext = createContext<PremiumFeatures[] | null>(
  null,
);

interface BoardPremiumFeaturesProviderProps {
  boardId: string | null | undefined;
}

export const BoardPremiumFeaturesProvider: FunctionComponent<
  PropsWithChildren<BoardPremiumFeaturesProviderProps>
> = ({ boardId = '', children }) => {
  const { data: fragmentData, complete } = useFragment({
    fragment: BoardPremiumFeaturesFragmentDoc,
    fragmentName: 'BoardPremiumFeatures',
    from: boardId
      ? {
          id: boardId,
          __typename: 'Board',
        }
      : null,
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const data = complete ? fragmentData : undefined;

  return (
    <BoardPremiumFeaturesContext.Provider value={data?.premiumFeatures || []}>
      {children}
    </BoardPremiumFeaturesContext.Provider>
  );
};

export const useIsBoardPremiumFeatureEnabled = (
  feature: PremiumFeatures,
): boolean => {
  const premiumFeatures = useContext(BoardPremiumFeaturesContext);

  if (premiumFeatures === null) {
    throw new Error(
      'Could not find board premium features in the React context. Did you forget to wrap the root component in a <BoardPremiumFeaturesProvider>?',
    );
  }

  return premiumFeatures.includes(feature);
};
